import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Star } from 'lucide-react';
import { format } from 'date-fns';

function WeeklyCalendar() {
  const [weeklyData, setWeeklyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchWeeklyInsights();
  }, []);

  const fetchWeeklyInsights = async () => {
    try {
      const response = await axios.get('/api/insights/weekly');
      setWeeklyData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load weekly insights');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#10B981';
    if (score >= 60) return '#F59E0B';
    return '#9CA3AF';
  };

  if (loading) {
    return (
      <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="loader" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="container" style={{ maxWidth: '600px', textAlign: 'center' }}>
          <div className="alert alert-warning">{error}</div>
          <button onClick={() => navigate('/data-input')} className="btn btn-primary" style={{ marginTop: '1rem' }}>
            Complete Profile First
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container" style={{ maxWidth: '900px' }}>
        <button
          onClick={() => navigate('/dashboard')}
          className="btn btn-ghost"
          style={{ marginBottom: '2rem' }}
          data-testid="back-button"
        >
          <ArrowLeft size={20} style={{ marginRight: '0.5rem' }} />
          Dashboard
        </button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>Weekly Insights</h1>
          <p style={{ color: '#9CA3AF', marginBottom: '2rem' }}>Your best days this week based on numerology</p>
        </motion.div>

        {weeklyData && weeklyData.summary && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="card"
            style={{ marginBottom: '2rem', background: 'rgba(0, 217, 255, 0.05)' }}
          >
            <h3 style={{ fontSize: '1.25rem', marginBottom: '0.75rem' }}>This Week's Energy</h3>
            <p>{weeklyData.summary}</p>
          </motion.div>
        )}

        {weeklyData && weeklyData.weekly_insights && (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {weeklyData.weekly_insights.map((day, index) => (
              <motion.div
                key={day.date}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="card"
                style={{
                  border: day.score >= 80 ? `2px solid ${getScoreColor(day.score)}` : '1px solid #2D3748',
                  background: day.score >= 80 ? 'rgba(16, 185, 129, 0.05)' : '#151827'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                  <div>
                    <h3 style={{ fontSize: '1.25rem', marginBottom: '0.25rem' }}>{day.day_name}</h3>
                    <p style={{ fontSize: '0.875rem', color: '#9CA3AF', marginBottom: '0.75rem' }}>
                      {format(new Date(day.date), 'MMM dd, yyyy')}
                    </p>
                    <p style={{ fontSize: '0.875rem', color: '#D1D5DB' }}>{day.recommendation}</p>
                  </div>

                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      width: '70px',
                      height: '70px',
                      borderRadius: '50%',
                      border: `4px solid ${getScoreColor(day.score)}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '0.5rem'
                    }}>
                      <span style={{ fontSize: '1.5rem', fontWeight: 700, color: getScoreColor(day.score) }}>
                        {day.score}
                      </span>
                    </div>
                    {day.tags && day.tags.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', justifyContent: 'center' }}>
                        {day.tags.map((tag, i) => (
                          <span key={i} className="badge badge-primary" style={{ fontSize: '0.75rem' }}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default WeeklyCalendar;